import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "../lib/api";
import { useAuth } from "./AuthContext";

const CartContext = createContext(null);

const LS_KEY = "avx_cart";

const readGuestCart = () => {
    try {
        const raw = localStorage.getItem(LS_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const writeGuestCart = (items) => {
    try { localStorage.setItem(LS_KEY, JSON.stringify(items)); } catch { /* ignore */ }
};

/**
 * Cart state is unified into `cart`:
 *   { items: [...hydrated], subtotal_clp, subtotal_usd, count }
 *
 * When logged in, items come from the server (with resolved product/bundle data).
 * When logged out, items are stored in localStorage as [{product_id?, bundle_id?}]
 *  — we hydrate them on-the-fly by fetching the public /products and /bundles endpoints.
 */
export function CartProvider({ children }) {
    const { user, loading: authLoading } = useAuth();
    const isAuthed = !!user && user !== false;

    const [cart, setCart] = useState({ items: [], items_usd: [], subtotal_clp: 0, subtotal_usd: 0, count: 0 });
    const [ready, setReady] = useState(false);

    // Hydrate guest items using the public product/bundle endpoints.
    const hydrateGuestCart = useCallback(async () => {
        const raw = readGuestCart();
        if (raw.length === 0) {
            setCart({ items: [], items_usd: [], subtotal_clp: 0, subtotal_usd: 0, count: 0 });
            return;
        }
        try {
            const [{ data: products }, { data: bundles }] = await Promise.all([
                api.get("/products"),
                api.get("/bundles").catch(() => ({ data: [] })),
            ]);
            const pMap = new Map((products || []).map((p) => [p.id, p]));
            const bMap = new Map((bundles || []).map((b) => [b.id, b]));
            const items = [];
            const items_usd = [];
            let sub_clp = 0, sub_usd = 0;
            for (const it of raw) {
                let target = null;
                let kind = null;
                if (it.product_id && pMap.has(it.product_id)) {
                    target = pMap.get(it.product_id);
                    kind = "product";
                } else if (it.bundle_id && bMap.has(it.bundle_id)) {
                    target = bMap.get(it.bundle_id);
                    kind = "bundle";
                }
                if (!target) continue;
                const baseC = Number(target.price_clp || 0);
                const saleC = Number(target.sale_price_clp || 0);
                const effC = saleC > 0 && saleC < baseC ? saleC : baseC;
                const baseU = Number(target.price_usd || 0);
                const saleU = Number(target.sale_price_usd || 0);
                const effU = saleU > 0 && saleU < baseU ? saleU : baseU;
                const key = kind === "product" ? `p:${target.id}` : `b:${target.id}`;
                items.push({
                    key, kind,
                    product_id: kind === "product" ? target.id : null,
                    bundle_id: kind === "bundle" ? target.id : null,
                    name: target.name, slug: target.slug, image_url: target.image_url || "",
                    base_price: baseC, sale_price: saleC > 0 && saleC < baseC ? saleC : 0, effective_price: effC,
                });
                items_usd.push({
                    key, kind,
                    product_id: kind === "product" ? target.id : null,
                    bundle_id: kind === "bundle" ? target.id : null,
                    name: target.name, slug: target.slug, image_url: target.image_url || "",
                    base_price: baseU, sale_price: saleU > 0 && saleU < baseU ? saleU : 0, effective_price: effU,
                });
                sub_clp += effC;
                sub_usd += effU;
            }
            setCart({ items, items_usd, subtotal_clp: sub_clp, subtotal_usd: Math.round(sub_usd * 100) / 100, count: items.length });
        } catch {
            setCart({ items: [], items_usd: [], subtotal_clp: 0, subtotal_usd: 0, count: 0 });
        }
    }, []);

    const refreshServerCart = useCallback(async () => {
        try {
            const { data } = await api.get("/cart");
            setCart(data);
        } catch {
            /* ignore */
        }
    }, []);

    // On mount + auth change: load from correct source. If user just logged in and has guest items → merge.
    useEffect(() => {
        if (authLoading) return;
        (async () => {
            if (isAuthed) {
                const guest = readGuestCart();
                if (guest.length > 0) {
                    try {
                        await api.post("/cart/merge", { items: guest });
                        localStorage.removeItem(LS_KEY);
                    } catch {
                        /* ignore merge failures */
                    }
                }
                await refreshServerCart();
            } else {
                await hydrateGuestCart();
            }
            setReady(true);
        })();
    }, [isAuthed, authLoading, refreshServerCart, hydrateGuestCart]);

    const addItem = useCallback(async ({ product_id, bundle_id }) => {
        if (isAuthed) {
            try {
                const { data } = await api.post("/cart/items", { product_id, bundle_id });
                setCart(data);
                toast.success("Added to cart");
                return { ok: true };
            } catch (e) {
                const msg = apiErrorMessage(e);
                toast.error(msg);
                return { ok: false, error: msg };
            }
        }
        // Guest mode
        const raw = readGuestCart();
        // Dedupe
        if (product_id && raw.some((it) => it.product_id === product_id)) {
            toast.error("This plugin is already in the cart.");
            return { ok: false, error: "duplicate" };
        }
        if (bundle_id && raw.some((it) => it.bundle_id === bundle_id)) {
            toast.error("This bundle is already in the cart.");
            return { ok: false, error: "duplicate" };
        }
        raw.push({ product_id: product_id || null, bundle_id: bundle_id || null, added_at: new Date().toISOString() });
        writeGuestCart(raw);
        await hydrateGuestCart();
        toast.success("Added to cart");
        return { ok: true };
    }, [isAuthed, hydrateGuestCart]);

    const removeItem = useCallback(async (item_key) => {
        if (isAuthed) {
            try {
                const { data } = await api.delete(`/cart/items/${item_key}`);
                setCart(data);
            } catch (e) {
                toast.error(apiErrorMessage(e));
            }
            return;
        }
        const raw = readGuestCart();
        const filtered = raw.filter((it) => {
            const k = it.product_id ? `p:${it.product_id}` : `b:${it.bundle_id}`;
            return k !== item_key;
        });
        writeGuestCart(filtered);
        await hydrateGuestCart();
    }, [isAuthed, hydrateGuestCart]);

    const clear = useCallback(async () => {
        if (isAuthed) {
            try {
                const { data } = await api.post("/cart/clear");
                setCart(data);
            } catch { /* ignore */ }
            return;
        }
        writeGuestCart([]);
        setCart({ items: [], items_usd: [], subtotal_clp: 0, subtotal_usd: 0, count: 0 });
    }, [isAuthed]);

    const inCart = useCallback((refKey /* "p:<id>" or "b:<id>" */) => {
        return (cart.items || []).some((it) => it.key === refKey);
    }, [cart]);

    return (
        <CartContext.Provider value={{ cart, ready, addItem, removeItem, clear, inCart, refreshServerCart }}>
            {children}
        </CartContext.Provider>
    );
}

export function useCart() {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error("useCart must be used within CartProvider");
    return ctx;
}
